import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Skeleton loading component for asset grids.
 * Displays animated placeholder cards while assets are loading.
 */
@Component({
	selector: 'app-asset-skeleton',
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="skeleton-grid" [attr.aria-label]="'Loading ' + count + ' assets'" role="status">
			<div class="skeleton-card" *ngFor="let item of skeletonItems; trackBy: trackByIndex">
				<div class="skeleton-thumbnail shimmer"></div>
				<div class="skeleton-content">
					<div class="skeleton-title shimmer"></div>
					<div class="skeleton-meta shimmer"></div>
				</div>
			</div>
			<span class="sr-only">Loading assets...</span>
		</div>
	`,
	styles: [`
		:host {
			display: block;
			width: 100%;
		}

		.sr-only {
			position: absolute;
			width: 1px;
			height: 1px;
			padding: 0;
			margin: -1px;
			overflow: hidden;
			clip: rect(0, 0, 0, 0);
			white-space: nowrap;
			border: 0;
		}

		.skeleton-grid {
			display: grid;
			grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
			gap: 16px;
			padding: 16px;
		}

		.skeleton-card {
			border-radius: 8px;
			overflow: hidden;
			background: var(--foreground-color, #1a1a1a);
		}

		.skeleton-thumbnail {
			width: 100%;
			height: 140px;
			background: var(--color-step-700, #2a2a2a);
		}

		.skeleton-content {
			padding: 12px;
		}

		.skeleton-title {
			height: 14px;
			background: var(--color-step-700, #2a2a2a);
			margin-bottom: 8px;
			width: 85%;
			border-radius: 4px;
		}

		.skeleton-meta {
			height: 10px;
			background: var(--color-step-700, #2a2a2a);
			width: 60%;
			border-radius: 4px;
		}

		.shimmer {
			background: linear-gradient(
				90deg,
				var(--color-step-700, #2a2a2a) 25%,
				var(--color-step-600, #3a3a3a) 50%,
				var(--color-step-700, #2a2a2a) 75%
			);
			background-size: 200% 100%;
			animation: shimmer 1.5s infinite;
		}

		@keyframes shimmer {
			0% { background-position: 200% 0; }
			100% { background-position: -200% 0; }
		}

		@media (prefers-reduced-motion: reduce) {
			.shimmer {
				animation: none;
			}
		}

		@media (max-width: 600px) {
			.skeleton-grid {
				grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
				gap: 12px;
				padding: 12px;
			}

			.skeleton-thumbnail {
				height: 100px;
			}
		}
	`]
})
export class AssetSkeletonComponent {
	@Input() count = 8;

	get skeletonItems(): number[] {
		return Array.from({ length: this.count }, (_, i) => i);
	}

	trackByIndex(index: number): number {
		return index;
	}
}
